import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
var _process$env$NEXT_PUB;
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { useMediaQuery } from "react-responsive";
import moment from "moment";

// Components
import { MuscleButton } from "@modules/shared/components/buttons";
import MuscleDateRangeCalendar from "@modules/shared/components/date-calendars/date-range-calendar";
import MuscleFormDateInput from "@modules/shared/components/form-date-input";

// Hooks and Context
import { useSideOverPanel } from "@modules/mobile/hooks/useSideOverPanel";
import { useHotelsSearch } from "@lib/context/hotels/search-context";
import { useMobileFilters } from "@lib/context/mobile/filters-context";

// Types and Utils

import { BREAKPOINTS } from "@lib/constants";
import useCssModules from "@lib/hooks/use-css-modules";
import { usePathname } from "next/navigation";
import { useForwardNode } from "@modules/mobile/hooks/useForwardNode";

// Constants
const MAX_DAYS = Number((_process$env$NEXT_PUB = process.env.NEXT_PUBLIC_MAX_CALENDAR_HOTEL_DAYS) !== null && _process$env$NEXT_PUB !== void 0 ? _process$env$NEXT_PUB : "365") || 365;

// Custom Hooks
const useCalendarConfig = () => {
  const minDate = moment().toDate();
  const maxDate = moment().add(MAX_DAYS, "days").toDate();
  return {
    minDate,
    maxDate
  };
};
const useHotelsInfo = () => {
  const {
    search,
    destinationSelection
  } = useHotelsSearch();
  return useMemo(() => {
    var _search$destination;
    if (search !== null && search !== void 0 && (_search$destination = search.destination) !== null && _search$destination !== void 0 && _search$destination.id && destinationSelection) {
      return {
        destination: search.destination,
        dates: search.dates,
        occupations: search.occupations
      };
    }
    return {
      destination: {
        id: "",
        value: ""
      },
      dates: null,
      occupations: [{
        adults: 1,
        children: 0,
        children_ages: []
      }]
    };
  }, [search, destinationSelection]);
};

// Main Component
export const HotelsCalendarSelection = () => {
  const t = useTranslations("Shared");
  const isMobile = useMediaQuery({
    maxWidth: BREAKPOINTS.md - 1
  });
  const {
    search,
    setSearch
  } = useHotelsSearch();
  const {
    hotelsFilters
  } = useMobileFilters();
  const {
    togglePanel
  } = useSideOverPanel();
  const pathname = usePathname();
  const {
    cssModules
  } = useCssModules();
  const {
    setNode
  } = useForwardNode();
  const {
    calendarSelectionPanelContainer,
    calendarSelectionApplyButtonContainer,
    calendarSelectionInputsContainer,
    calendarSelectionPricesContainer
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.views.mobile.CalendarSelection) || {};

  // Configuration and state
  const {
    minDate,
    maxDate
  } = useCalendarConfig();
  const currentHotelsInfo = useHotelsInfo();

  // Selected dates state - using DateRange format
  const {
    0: selectedDateRange,
    1: setSelectedDateRange
  } = useState({
    from: undefined,
    to: undefined
  });

  // Update selected dates when context changes
  useEffect(() => {
    var _search$dates, _search$dates2;
    if (search !== null && search !== void 0 && (_search$dates = search.dates) !== null && _search$dates !== void 0 && _search$dates.check_in && search !== null && search !== void 0 && (_search$dates2 = search.dates) !== null && _search$dates2 !== void 0 && _search$dates2.check_out) {
      setSelectedDateRange({
        from: new Date(search.dates.check_in),
        to: new Date(search.dates.check_out)
      });
    }
  }, [search === null || search === void 0 ? void 0 : search.dates]);

  // Event handlers
  const handleChangeDate = useCallback((from, to) => {
    setSelectedDateRange({
      from,
      to
    });
    if (from && to) {
      const updatedDates = {
        check_in: from,
        check_out: to
      };
      setSearch(prev => prev ? _objectSpread(_objectSpread({}, prev), {}, {
        dates: updatedDates
      }) : {
        destination: currentHotelsInfo.destination,
        dates: updatedDates,
        occupations: hotelsFilters.selectedOccupations
      });
    }
  }, [search, currentHotelsInfo, hotelsFilters, setSearch]);
  const handleCalendarSelect = useCallback(dateRange => {
    handleChangeDate(dateRange.from, dateRange.to);
  }, [handleChangeDate]);
  const isDateSelectionValid = useMemo(() => {
    return selectedDateRange.from !== undefined && selectedDateRange.to !== undefined;
  }, [selectedDateRange]);

  // Render helpers
  const renderCalendar = () => {
    return __jsx(MuscleDateRangeCalendar, {
      minDate: minDate,
      maxDate: maxDate,
      isMobile: isMobile,
      onSelect: handleCalendarSelect,
      value: selectedDateRange,
      behavior: "default"
    });
  };
  const renderDateInputs = () => {
    var _selectedDateRange$fr, _selectedDateRange$to, _selectedDateRange$fr2;
    return __jsx("div", {
      className: calendarSelectionInputsContainer
    }, __jsx(MuscleFormDateInput, {
      value: (_selectedDateRange$fr = selectedDateRange.from) === null || _selectedDateRange$fr === void 0 ? void 0 : _selectedDateRange$fr.toISOString(),
      onChange: dateString => {
        const date = dateString ? new Date(dateString) : undefined;
        handleChangeDate(date, selectedDateRange.to);
      },
      minDate: minDate.toISOString(),
      maxDate: maxDate.toISOString(),
      className: "flex-1 !max-w-[100px]",
      hideEmptyLabel: true,
      hideGuideText: true,
      hideErrorMessage: true
    }), __jsx("span", {
      className: "text-primary_text tx-pmd-regular"
    }, t("TO_THE")), __jsx(MuscleFormDateInput, {
      value: (_selectedDateRange$to = selectedDateRange.to) === null || _selectedDateRange$to === void 0 ? void 0 : _selectedDateRange$to.toISOString(),
      onChange: dateString => {
        const date = dateString ? new Date(dateString) : undefined;
        handleChangeDate(selectedDateRange.from, date);
      },
      minDate: ((_selectedDateRange$fr2 = selectedDateRange.from) === null || _selectedDateRange$fr2 === void 0 ? void 0 : _selectedDateRange$fr2.toISOString()) || minDate.toISOString(),
      maxDate: maxDate.toISOString(),
      className: "flex-1 !max-w-[100px]",
      hideEmptyLabel: true,
      hideGuideText: true,
      hideErrorMessage: true
    }));
  };
  const renderButtons = () => __jsx("div", {
    className: calendarSelectionApplyButtonContainer
  }, __jsx(MuscleButton, {
    dataTestId: "calendar-choose-dates-button",
    onClick: pathname.includes("/hotels/search") ? () => setNode("EditHotelSelection") : togglePanel,
    disabled: !isDateSelectionValid,
    variant: "solidXL"
  }, t("APPLY")), pathname.includes("/hotels/search") ? __jsx(MuscleButton, {
    dataTestId: "calendar-cancel-button",
    onClick: () => setNode("EditHotelSelection"),
    variant: "outlineXL"
  }, t("MAKE_CHANGES")) : __jsx(MuscleButton, {
    dataTestId: "calendar-cancel-button",
    onClick: togglePanel,
    variant: "outlineXL"
  }, t("CANCEL")));
  return __jsx("div", {
    className: calendarSelectionPanelContainer
  }, renderCalendar(), renderDateInputs(), renderButtons());
};